import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import { VisuallyHidden } from "../visually-hidden"
import { useNavigationContext } from "./navigation-context"
import { matchesPath, resolveActiveIcon } from "./navigation-utils"
import { NavigationLink } from "./navigation-link"
import { NavigationBadge, type NavigationBadgeType } from "./navigation-badge"

// =============================================================================
// Props
// =============================================================================

export type NavigationItemProps = {
  /**
   * Leading content. Pass an `IconName` string for the common case, or a
   * `React.ReactElement` to render a custom element (e.g. `ProgressCircle`).
   * Shown in both expanded and collapsed modes.
   */
  leading: IconName | React.ReactElement
  label: string
  /**
   * Pathname prefix to match against `Navigation`'s `currentPath`. The item
   * is active when `currentPath.startsWith(pathPrefix)`, so sub-pages (tabs,
   * detail views) under the same prefix are covered automatically.
   */
  pathPrefix?: string
  /**
   * Description shown in the tooltip on hover. When `badge` is set, the
   * tooltip also renders a coloured "Paid feature" / "Beta feature" callout
   * beneath the description.
   */
  tooltip?: string
  render?: useRender.RenderProp
  /**
   * Flags the item as a paid or beta feature displaying an icon
   */
  badge?: NavigationBadgeType
  /**
   * Renders at the right edge of the row. Hidden when the sidebar is
   * collapsed. Use for counters or other trailing decorations.
   */
  trailing?: React.ReactNode
}

// =============================================================================
// Component
// =============================================================================

/**
 * A flat, non-expandable entry in the sidebar navigation.
 * Must be placed inside `Navigation.Menu` to produce valid `<ul>` / `<li>` markup.
 *
 * Renders a leading icon and a text label. In collapsed mode the label becomes
 * screen-reader-only and a tooltip appears on hover so sighted users can
 * still identify the item.
 *
 * Pass a `render` prop to inject your own link component (e.g. Inertia `Link`)
 * so the DS doesn't need to know about routing.
 *
 * @example
 * <Navigation.Menu>
 *   <Navigation.Item leading="home" label="Dashboard" pathPrefix="/dashboard" render={<Link href="/dashboard" />} />
 *   <Navigation.Item
 *     leading="email"
 *     label="Broadcasts"
 *     pathPrefix="/broadcasts"
 *     render={<Link href="/broadcasts" />}
 *   />
 * </Navigation.Menu>
 */
export function NavigationItem({
  leading,
  label,
  pathPrefix,
  tooltip,
  render,
  badge,
  trailing
}: NavigationItemProps) {
  const { collapsed, currentPath } = useNavigationContext()
  const isActive = matchesPath(currentPath, pathPrefix)

  return (
    <li data-slot="navigation-item">
      <NavigationLink
        label={label}
        isActive={isActive}
        tooltip={tooltip}
        badge={badge}
        render={render}
      >
        {typeof leading === "string" ? (
          <Icon
            name={isActive ? resolveActiveIcon(leading) : leading}
            className="shrink-0"
          />
        ) : (
          leading
        )}
        {collapsed ? (
          <VisuallyHidden>{label}</VisuallyHidden>
        ) : (
          <span className="flex min-w-0 items-center gap-x-2">
            <span className="flex min-w-0 flex-1 items-center gap-x-2">
              <span className="min-w-0 truncate text-left font-semibold">
                {label}
              </span>
              {badge && <NavigationBadge type={badge} />}
            </span>
            {trailing}
          </span>
        )}
      </NavigationLink>
    </li>
  )
}

NavigationItem.displayName = "Navigation.Item"
